import { JSONSchema7 } from 'json-schema';
import addAjvFormats from 'ajv-formats';
import addAjvErrorMessages from 'ajv-errors';
import Ajv from 'ajv';

/*
 * This is the meta-schema for canonical schemas and reference data schemas, which
 * are based on JSON Schema Draft 7, with some proprietary extensions and restrictions. Those
 * extensions are what we're documenting here.
 *
 *                                    *******************
 *                                    *** PLEASE NOTE ***
 *                                    *******************
 *
 * If you make changes to this, please do whatever it takes to be backwards compatible.  Also
 * please update the version number in the $id field.
 */
const CanonicalMetaSchema: JSONSchema7 = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'http://watershedclimate.com/canonical/v0.01/schema#',
  title: 'Watershed canonical schema meta-schema',
  definitions: {
    schemaArray: {
      type: 'array',
      minItems: 1,
      items: { $ref: '#' },
    },
    nonNegativeInteger: {
      type: 'integer',
      minimum: 0,
    },
    nonNegativeIntegerDefault0: {
      allOf: [{ $ref: '#/definitions/nonNegativeInteger' }, { default: 0 }],
    },
    simpleTypes: {
      enum: ['object', 'boolean', 'integer', 'number', 'string'],
    },
    stringArray: {
      type: 'array',
      items: { type: 'string' },
      uniqueItems: true,
      default: [],
    },
    standardizerArray: {
      type: 'array',
      items: {
        type: 'string',
        enum: ['HiggYarnDtex'],
      },
      uniqueItems: true,
      default: [],
    },
    field: {
      type: 'object',
      properties: {
        type: {
          enum: ['boolean', 'number', 'integer', 'string'],
        },
        description: {
          type: 'string',
        },
        format: {
          enum: [
            'date',
            'date-time',
            'us-date',
            'eu-date',
            'us-date-time',
            'eu-date-time',
            'us-number',
            'eu-number',
            'epoch-days',
            'epoch-seconds',
            'epoch-millis',
          ],
        },
        enum: {
          type: 'array',
          minItems: 1,
          uniqueItems: true,
          items: {
            oneOf: [{ type: 'string' }, { type: 'number' }, { type: 'null' }],
          },
        },
        examples: {
          type: 'array',
          items: { oneOf: [{ type: 'string' }, { type: 'number' }] },
        },
        default: {
          oneOf: [{ type: 'string' }, { type: 'number' }, { type: 'boolean' }],
        },
        deprecated: {
          type: 'boolean',
          default: false,
        },
        storeAsHundredth: {
          type: 'boolean',
          default: false,
        },
        displayName: {
          type: 'string',
        },
        nullable: {
          type: 'boolean',
        },
        errorMessage: {
          type: 'string',
        },
        unit: {
          type: 'string',
        },
        unitColumn: {
          type: 'string',
        },
      },
      required: ['type', 'description'],
    },
  },
  type: 'object',
  properties: {
    $id: {
      type: 'string',
      format: 'uri-reference',
    },
    $schema: {
      type: 'string',
      format: 'uri',
    },
    $ref: {
      type: 'string',
      format: 'uri-reference',
    },
    $comment: {
      type: 'string',
    },
    title: {
      type: 'string',
    },
    description: {
      type: 'string',
    },
    required: { $ref: '#/definitions/stringArray' },
    standardizerOutputOnly: { $ref: '#/definitions/stringArray' },
    standardizers: { $ref: '#/definitions/standardizerArray' },
    uniqueKeys: { $ref: '#/definitions/stringArray' },
    additionalProperties: { $ref: '#' },
    properties: {
      type: 'object',
      additionalProperties: {
        $ref: '#/definitions/field',
      },
    },
    patternProperties: {
      type: 'object',
      additionalProperties: { $ref: '#/definitions/field' },
      propertyNames: { format: 'regex' },
      default: {},
    },
    dependencies: {
      type: 'object',
      additionalProperties: {
        anyOf: [{ $ref: '#' }, { $ref: '#/definitions/stringArray' }],
      },
    },
    type: {
      const: 'object',
    },
  },
  required: ['type', 'properties'],
  default: true,
};

export default CanonicalMetaSchema;

export function canonicalMetaSchemaAjv({ forCompilation = false } = {}): Ajv {
  const ajv = new Ajv({
    allErrors: true,
    verbose: true,
    strictSchema: false,
    strictTypes: true,
    allowUnionTypes: true,
    useDefaults: false,
    coerceTypes: false,
    ...(forCompilation ? { code: { source: true } } : {}),
  });
  addAjvFormats(ajv, {
    mode: 'fast',
    formats: ['date', 'date-time', 'uri', 'uri-reference', 'regex'],
  });
  addAjvErrorMessages(ajv);
  return ajv;
}
